import { ReactNode, useMemo } from 'react'

import {
  BusinessPackageHeroRebrand,
  ColourAndImageHeroRebrand,
  WidgetHeroRebrand
} from 'src/components/layouts/HeroRebrand'
import ShortHeroRebrand from 'src/components/layouts/HeroRebrand/ShortHeroRebrand'
import StandardHeroRebrand from 'src/components/layouts/HeroRebrand/StandardHeroRebrand'
import { HeroRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { CmsCtaButtonRebrand } from 'src/components/molecules/CmsCtaButtonRebrand'
import { DonateWidget } from 'src/components/organisms/DonateWidget'

import { getSanityImageUrl } from 'src/utils/sanity'

export type SanityHeroRebrandProps = HeroRebrandResult

const widgetMap: Record<NonNullable<HeroRebrandResult['widget']>, ReactNode> = {
  donateWidget: <DonateWidget />
}

const SanityHeroRebrand = ({
  title,
  color,
  eyebrow,
  imageUrl,
  intro,
  mode,
  layout,
  unified,
  pattern,
  widget,
  darkHeader,
  monthlyAmount,
  annualAmount,
  ctas
}: SanityHeroRebrandProps) => {
  const ctaNodes = useMemo<ReactNode>(
    () =>
      ctas?.length
        ? ctas.map((cta, index) => (
            <CmsCtaButtonRebrand key={`hero-cta-${index}`} {...cta} />
          ))
        : null,
    [ctas]
  )

  // Guard: editors may add a hero before uploading its image. Pass an empty
  // src in that case instead of crashing on `imageUrl!.asset`.
  const resolvedImageUrl = imageUrl?.asset
    ? getSanityImageUrl(imageUrl.asset)
    : ''

  switch (layout) {
    case 'standard':
      return (
        <StandardHeroRebrand
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          imageUrl={resolvedImageUrl}
          ctas={ctaNodes}
        />
      )
    case 'short':
      return (
        <ShortHeroRebrand
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          ctas={ctaNodes}
          {...(pattern
            ? { layout: 'pattern', pattern }
            : { layout: 'solid', color: color! })}
        />
      )
    case 'colourAndImage':
      return (
        <ColourAndImageHeroRebrand
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          layout='pattern'
          pattern={pattern!}
          imageUrl={resolvedImageUrl}
          ctas={ctaNodes}
        />
      )
    case 'businessPackage':
      return (
        <BusinessPackageHeroRebrand
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          layout='pattern'
          pattern={pattern!}
          imageUrl={resolvedImageUrl}
          ctas={ctaNodes}
          monthlyAmount={monthlyAmount!}
          annualAmount={annualAmount!}
        />
      )
    case 'headlineWithWidget':
      return (
        <WidgetHeroRebrand
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          layout='solid'
          color={color!}
          ctas={ctaNodes}
          widget={widget && widgetMap[widget]}
        />
      )
    case 'standardWidget':
      return (
        <WidgetHeroRebrand
          layout='image'
          title={title}
          eyebrow={eyebrow}
          intro={intro}
          mode={mode}
          unified={unified}
          darkHeader={darkHeader}
          imageUrl={resolvedImageUrl}
          ctas={ctaNodes}
          widget={widget && widgetMap[widget]}
        />
      )
    default:
      return null
  }
}

export default SanityHeroRebrand
